import type {CreativeWork, Graph, InteractionCounter, Person, Thing} from 'schema-dts'

import {buildGraph, buildWebSite, notabilityBrandNodes} from './structuredData'
import {TriageState} from './types'
import {absoluteUrl, stripTrailingSlash} from './urls'

// The two origins a gallery graph is anchored on. Builders take them as arguments and read no
// config of their own, so the gallery front end and the api service — which serves the same notes
// from EJS with no React runtime — describe a note with byte-identical markup.
export interface GalleryGraphOrigins {
  // The gallery's public origin, basePath included (e.g. https://notability.com/gallery).
  galleryUrl: string
  // Anchors the shared brand entity; see `notabilityBrandIds`. Every property passes the same one.
  apiServiceUrl: string
}

// Gallery pages emit the Organization + SoftwareApplication built by the shared
// `notabilityBrandNodes`, anchored on `API_SERVICE_URL` exactly as the marketing site anchors
// them (see `notabilityBrandIds`), so the gallery's public traffic reinforces the same Notability
// brand/product entity rather than a separate one. The gallery's own WebSite keeps a
// gallery-origin `@id` but publishes under that shared Organization.
export function galleryGraphContext({galleryUrl, apiServiceUrl}: GalleryGraphOrigins) {
  const origin = stripTrailingSlash(galleryUrl)
  const brand = notabilityBrandNodes({apiServiceUrl})
  const websiteId = `${origin}/#website`
  const nodes: Thing[] = [
    brand.organization,
    buildWebSite({
      // A product name, not translated copy — the gallery is branded the same in every locale.
      websiteId,
      url: origin,
      name: 'Notability Gallery',
      publisherId: brand.ids.organization
    }),
    brand.softwareApplication
  ]
  const profilePath = (screenname: string) => `/profile/${screenname}`
  return {
    websiteId,
    organizationId: brand.ids.organization,
    nodes,
    pageUrl: (path: string) => absoluteUrl(origin, path),
    noteUrl: (noteId: string) => absoluteUrl(origin, `/${noteId}`),
    profilePath,
    profileUrl: (screenname: string) => absoluteUrl(origin, profilePath(screenname))
  }
}

export interface GalleryNoteStructuredData {
  id: string
  name: string
  description?: string | null
  images: string[]
  postedDate: string
  language?: string | null
  parentNoteId?: string | null
  triageState: TriageState
  underFlaggingReview: boolean
  // Gates the whole graph, not just the author node: a hidden creator's notes are not described to
  // crawlers at all. Every caller can answer this from the note's owner without resolving a profile.
  authorProfileHidden: boolean
  // Omitted by callers that don't resolve the creator's screenname — the work then carries no
  // `author`, rather than an author the caller had to guess at. `displayName` keeps the emitted
  // `name` in agreement with the profile page's `Person` node, which shares this `@id`.
  author?: {screenname: string; displayName?: string | null; avatarUrl?: string | null}
  tags?: {name: string}[]
  // Omitted rather than zeroed by a caller that doesn't have the counts. schema.org interaction
  // counts are read as claims of fact, so silence beats a fabricated zero.
  likes?: number
  numComments?: number
}

function interactionCounters(note: GalleryNoteStructuredData): InteractionCounter[] {
  const counters: InteractionCounter[] = []
  if (note.likes !== undefined) {
    counters.push({
      '@type': 'InteractionCounter',
      interactionType: {'@type': 'LikeAction'},
      userInteractionCount: note.likes
    })
  }
  if (note.numComments !== undefined) {
    counters.push({
      '@type': 'InteractionCounter',
      interactionType: {'@type': 'CommentAction'},
      userInteractionCount: note.numComments
    })
  }
  return counters
}

// Returns the JSON-LD graph for a public gallery note, or `null` when the note must not be
// exposed to crawlers (not approved, under flagging review, or the author's profile is hidden).
//
// The `@id` and `url` are the note's gallery page even when another origin emits the markup, so
// every URL serving the note claims one entity rather than competing duplicates.
export function buildGalleryNoteGraph(origins: GalleryGraphOrigins, note: GalleryNoteStructuredData): Graph | null {
  if (note.triageState !== TriageState.APPROVED || note.underFlaggingReview || note.authorProfileHidden) {
    return null
  }

  const gallery = galleryGraphContext(origins)
  const noteUrl = gallery.noteUrl(note.id)
  // name/alternateName mirror `buildGalleryProfileGraph`: both emitters describe one Person `@id`,
  // so their claims about it must agree.
  const author: Person | undefined = note.author
    ? {
        '@type': 'Person',
        '@id': gallery.profileUrl(note.author.screenname),
        name: note.author.displayName || note.author.screenname,
        ...(note.author.displayName && note.author.displayName !== note.author.screenname
          ? {alternateName: note.author.screenname}
          : {}),
        url: gallery.profileUrl(note.author.screenname),
        ...(note.author.avatarUrl ? {image: note.author.avatarUrl} : {})
      }
    : undefined
  const interactionStatistic = interactionCounters(note)
  const work: CreativeWork = {
    '@type': 'CreativeWork',
    '@id': `${noteUrl}#note`,
    name: note.name,
    ...(note.description ? {description: note.description} : {}),
    url: noteUrl,
    ...(note.images.length ? {image: note.images} : {}),
    ...(author ? {author} : {}),
    datePublished: note.postedDate,
    ...(note.tags?.length ? {keywords: note.tags.map((tag) => tag.name)} : {}),
    ...(note.language ? {inLanguage: note.language} : {}),
    isPartOf: {'@id': gallery.websiteId},
    publisher: {'@id': gallery.organizationId},
    ...(interactionStatistic.length ? {interactionStatistic} : {}),
    // Remix lineage: links a remix back to the note it was based on (parent is itself gated to
    // APPROVED upstream), clarifying it is derivative rather than duplicate content.
    ...(note.parentNoteId ? {isBasedOn: gallery.noteUrl(note.parentNoteId)} : {})
  }
  return buildGraph([...gallery.nodes, work])
}
